import { NavLink } from 'react-router-dom'
import { Home, Building2, Users, Receipt, AlertCircle, Mail, Gauge, Upload, BarChart3, Bell, Settings, ChevronDown, ChevronRight, TrendingUp, FileText, History, Activity, Shield, CreditCard } from 'lucide-react'
import { useUiStore } from '@/stores/ui-store'
import { useAuthStore } from '@/stores/auth-store'
import { useState } from 'react'

interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  superuserOnly?: boolean
}

interface NavGroup {
  title: string
  items: NavItem[]
  defaultOpen?: boolean
  superuserOnly?: boolean
}

const navigationGroups: NavGroup[] = [
  {
    title: 'Główne',
    defaultOpen: true,
    items: [
      { name: 'Dashboard', href: '/', icon: Home },
    ]
  },
  {
    title: 'Zarządzanie',
    defaultOpen: true,
    items: [
      { name: 'Wspólnoty', href: '/wspolnoty', icon: Building2 },
      { name: 'Lokale', href: '/lokale', icon: Users },
      { name: 'Liczniki', href: '/liczniki', icon: Activity },
      { name: 'Odczyty', href: '/odczyty', icon: Gauge },
    ]
  },
  {
    title: 'Finanse',
    defaultOpen: true,
    items: [
      { name: 'Rozliczenia', href: '/rozliczenia', icon: Receipt },
      { name: 'Historia rozliczeń', href: '/historia-rozliczen', icon: History },
      { name: 'Pozycje czynszowe', href: '/pozycje-czynszowe', icon: FileText },
      { name: 'Stawki mediów', href: '/stawki-mediow', icon: TrendingUp },
      { name: 'Zadłużenia', href: '/zadluzenia', icon: AlertCircle },
      { name: 'Płatności', href: '/platnosci', icon: CreditCard },
      { name: 'Import bankowy', href: '/import-bankowy', icon: Upload },
      { name: 'Raporty', href: '/raporty', icon: BarChart3 },
    ]
  },
  {
    title: 'Komunikacja',
    defaultOpen: true,
    items: [
      { name: 'Powiadomienia', href: '/powiadomienia', icon: Bell },
      { name: 'Emaile', href: '/emaile', icon: Mail },
    ]
  },
  {
    title: 'System',
    defaultOpen: true,
    items: [
      { name: 'Administrator', href: '/administrator', icon: Settings },
    ]
  },
  {
    title: 'Administracja',
    defaultOpen: true,
    superuserOnly: true,
    items: [
      { name: 'Panel administratora', href: '/admin', icon: Shield, superuserOnly: true },
      { name: 'Dziennik zdarzeń', href: '/audit-log', icon: History, superuserOnly: true },
    ]
  },
]

export default function Sidebar() {
  const sidebarOpen = useUiStore((state) => state.sidebarOpen)
  const closeSidebar = useUiStore((state) => state.closeSidebar)
  const isSuperuser = useAuthStore((state) => state.user?.is_superuser ?? false)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    navigationGroups.reduce((acc, group) => ({ ...acc, [group.title]: group.defaultOpen ?? true }), {})
  )

  const toggleGroup = (title: string) => {
    setOpenGroups(prev => ({ ...prev, [title]: !prev[title] }))
  }

  // On mobile, clicking a nav link closes the overlay sidebar.
  const handleNavClick = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      closeSidebar()
    }
  }

  // Filter by superuser flag — hide groups/items available only to superusers.
  const visibleGroups = navigationGroups
    .filter((group) => !group.superuserOnly || isSuperuser)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.superuserOnly || isSuperuser),
    }))
    .filter((group) => group.items.length > 0)

  return (
    <>
      {/* Mobile backdrop — visible only on <md when sidebar open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/*
        Sidebar.
        - Desktop (md+): static positioned <aside> in layout flow is tricky with fixed navbar;
          we use `fixed` + main margin via Layout. Visible when sidebarOpen.
        - Mobile (<md): fixed overlay sliding from left. Hidden by translate when closed.
      */}
      <aside
        className={`
          fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200
          overflow-y-auto z-50 transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Menu główne"
        aria-hidden={!sidebarOpen}
      >
        <nav className="p-3 space-y-1">
          {visibleGroups.map((group) => (
            <div key={group.title} className="mb-2">
              {/* Group header - collapsible */}
              <button
                onClick={() => toggleGroup(group.title)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
              >
                <span>{group.title}</span>
                {openGroups[group.title] ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {/* Group items */}
              {openGroups[group.title] && (
                <div className="space-y-1 mt-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary text-white shadow-sm'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium text-sm">{item.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>
    </>
  )
}
